import React, { useMemo, useState, useEffect } from "react";
import {
    Row,
    Col,
    Typography,
    Space,
    Tag,
    Input,
    Select,
    DatePicker,
    Switch,
    Timeline,
    Card,
    Collapse,
    Empty,
    Tooltip,
    Divider,
} from "antd";
import {
    ThunderboltOutlined,
    BugOutlined,
    DatabaseOutlined,
    FireOutlined,
    CodeOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    FilterOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import fallbackUpdates from "./updates.json";
import { useTranslation } from "react-i18next";
import { asArray, asString } from "../../utils/dataGuards";

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const CHANGE_TYPE_META = {
    feature: { color: "green", label: "Feature", icon: <ThunderboltOutlined /> },
    improvement: { color: "blue", label: "Improve", icon: <CodeOutlined /> },
    bugfix: { color: "red", label: "Bugfix", icon: <BugOutlined /> },
    data: { color: "geekblue", label: "Data", icon: <DatabaseOutlined /> },
    docs: { color: "gold", label: "Docs", icon: <FileTextOutlined /> },
    perf: { color: "purple", label: "Perf", icon: <FireOutlined /> },
    security: { color: "magenta", label: "Security", icon: <InfoCircleOutlined /> },
    breaking: { color: "volcano", label: "Breaking", icon: <InfoCircleOutlined /> },
};

const SCOPE_OPTIONS = [
    { label: "Frontend", value: "frontend" },
    { label: "Backend", value: "backend" },
    { label: "Data", value: "data" },
    { label: "Infra", value: "infra" },
    { label: "Docs", value: "docs" },
];

const TYPE_OPTIONS = Object.keys(CHANGE_TYPE_META).map((k) => ({
    label: CHANGE_TYPE_META[k].label,
    value: k,
}));

const importanceTag = (imp) => {
    if (imp === "major") return <Tag color="volcano">MAJOR</Tag>;
    if (imp === "minor") return <Tag color="geekblue">MINOR</Tag>;
    return <Tag>PATCH</Tag>;
};

const ChangesList = ({ changes }) => {
    const safeChanges = asArray(changes);
    // 按 type 分组，展成 Collapse
    const groups = useMemo(() => {
        const m = {};
        safeChanges.forEach((c) => {
            if (!c || typeof c !== 'object') return;
            if (!m[c.type]) m[c.type] = [];
            m[c.type].push(c);
        });
        // 固定顺序（按 TYPE_OPTIONS）
        const order = {};
        TYPE_OPTIONS.forEach((o, idx) => (order[o.value] = idx));
        return Object.entries(m).sort((a, b) => (order[a[0]] ?? 99) - (order[b[0]] ?? 99));
    }, [safeChanges]);

    if (!safeChanges.length) return <Empty description="No changes yet" />;

    return (
        <Collapse ghost>
            {groups.map(([type, items]) => {
                const meta = CHANGE_TYPE_META[type] || {};
                return (
                    <Panel
                        header={
                            <Space size="small">
                                <Tag color={meta.color}>{meta.label}</Tag>
                                <Text type="secondary">Total: {items.length} entries</Text>
                            </Space>
                        }
                        key={type}
                    >
                        <Space direction="vertical" style={{ width: "100%" }}>
                            {items.map((c) => (
                                <Card
                                    key={c.id}
                                    size="small"
                                    hoverable
                                    bodyStyle={{ padding: 12 }}
                                >
                                    <Space wrap align="start" style={{ width: "100%", justifyContent: "space-between" }}>
                                        <Space size="middle" align="start">
                                            <Tag color={CHANGE_TYPE_META[c.type]?.color}>
                                                {CHANGE_TYPE_META[c.type]?.label}
                                            </Tag>
                                            <Tag>{c.scope}</Tag>
                                            <Text strong>{c.title}</Text>
                                        </Space>
                                        {asArray(c.tickets).length ? (
                                            <Space>
                                                {asArray(c.tickets).map((t) => (
                                                    <Tag key={t} color="default">
                                                        #{t}
                                                    </Tag>
                                                ))}
                                            </Space>
                                        ) : null}
                                    </Space>
                                </Card>
                            ))}
                        </Space>
                    </Panel>
                );
            })}
        </Collapse>
    );
};

const UpdatesPage = () => {
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [entries, setEntries] = useState([]);

    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState([]);
    const [scopeFilter, setScopeFilter] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [onlyBreaking, setOnlyBreaking] = useState(false);

    // 从 public 读取运行时 changelog，便于部署流程自动生成。
    useEffect(() => {
        setLoading(true);
        fetch(`${process.env.PUBLIC_URL || ""}/updates.json`, { cache: "no-cache" })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Updates file is unavailable");
                }
                return response.json();
            })
            .then((updates) => {
                setEntries(asArray(updates));
            })
            .catch(() => {
                setEntries(asArray(fallbackUpdates));
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        if (!loading && window.location.hash) {
            // URL hash 跳转至具体版本
            const id = window.location.hash.slice(1);
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [loading, entries]);

    const filtered = useMemo(() => {
        let list = [...asArray(entries)];

        // 过滤范围日期
        if (dateRange && dateRange.length === 2) {
            const [start, end] = dateRange;
            list = list.filter((v) => {
                const current = dayjs(v.date).valueOf();
                return (
                    current >= dayjs(start).startOf("day").valueOf() &&
                    current <= dayjs(end).endOf("day").valueOf()
                );
            });
        }

        // 只看 breaking
        if (onlyBreaking) {
            list = list
                .map((v) => ({
                    ...v,
                    changes: asArray(v?.changes).filter((c) => c?.type === "breaking"),
                }))
                .filter((v) => v.changes.length > 0);
        }

        // 类型筛选
        if (typeFilter.length) {
            list = list
                .map((v) => ({
                    ...v,
                    changes: asArray(v?.changes).filter((c) => typeFilter.includes(c?.type)),
                }))
                .filter((v) => v.changes.length > 0);
        }

        // 作用域筛选
        if (scopeFilter.length) {
            list = list
                .map((v) => ({
                    ...v,
                    changes: asArray(v?.changes).filter((c) => scopeFilter.includes(c?.scope)),
                }))
                .filter((v) => v.changes.length > 0);
        }

        // 搜索（版本号/overview/变更标题/详情）
        const keyword = asString(q).trim().toLowerCase();
        if (keyword) {
            list = list
                .map((v) => ({
                    ...v,
                    changes: asArray(v?.changes).filter(
                        (c) =>
                            asString(c?.title).toLowerCase().includes(keyword) ||
                            asString(c?.details).toLowerCase().includes(keyword)
                    ),
                }))
                .filter(
                    (v) =>
                        asString(v?.version).toLowerCase().includes(keyword) ||
                        asString(v?.overview).toLowerCase().includes(keyword) ||
                        v.changes.length > 0
                );
        }

        // 版本按日期倒序
        list.sort((a, b) => (dayjs(b.date).valueOf() - dayjs(a.date).valueOf()));
        return list;
    }, [entries, q, typeFilter, scopeFilter, dateRange, onlyBreaking]);

    return (
        <Row>
            <Col span={24} className="page-top-seize-seat" />
            <Col span={20} offset={2} style={{ marginTop: '6vh' }}>
                <span className="page-title">
                    { t('updates_title') }
                </span>
            </Col>
            <Col span={20} offset={2} style={{ marginTop: 24 }}>
                <Card size="small" bodyStyle={{ padding: 16 }}>
                    <Space wrap size="middle">
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Search version / title / details"
                            style={{ width: 300 }}
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Change Type"
                            style={{ minWidth: 300 }}
                            value={typeFilter}
                            onChange={setTypeFilter}
                            options={TYPE_OPTIONS}
                            suffixIcon={<FilterOutlined />}
                            maxTagCount="responsive"
                        />
                        <Select
                            mode="multiple"
                            allowClear
                            placeholder="Scope"
                            style={{ minWidth: 300 }}
                            value={scopeFilter}
                            onChange={setScopeFilter}
                            options={SCOPE_OPTIONS}
                            maxTagCount="responsive"
                        />
                        <RangePicker onChange={setDateRange} style={{ minWidth: 300 }} />
                        <Tooltip title="Show Breaking Changes Only">
                            <Space>
                                <Switch checked={onlyBreaking} onChange={setOnlyBreaking} />
                                <Text>Only Breaking</Text>
                            </Space>
                        </Tooltip>
                    </Space>
                </Card>
            </Col>
            <Col span={20} offset={2} style={{ marginTop: 24 }}>
                {loading ? (
                    <Card loading />
                ) : filtered.length === 0 ? (
                    <Empty description="No version records found" />
                ) : (
                    <Timeline style={{ marginLeft: '20px' }}>
                        {filtered.map((v) => (
                            <Timeline.Item
                                key={v.version}
                            >
                                <Card
                                    id={v.version}
                                    title={
                                        <Space size="large">
                                            <Space>
                                                <Text type="secondary">{dayjs(v.date).format("YYYY-MM-DD")}</Text>
                                                {importanceTag(v.importance)}
                                                <Text code style={{ fontSize: 16 }}>{v.version}</Text>
                                            </Space>
                                        </Space>
                                    }
                                    bordered
                                >
                                    {v.overview ? (
                                        <>
                                            <Paragraph style={{ marginBottom: 8 }}>{v.overview}</Paragraph>
                                            <Divider style={{ margin: "8px 0" }} />
                                        </>
                                    ) : null}
                                    <ChangesList changes={v.changes} />
                                </Card>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                )}
            </Col>
        </Row>
    );
};

export default UpdatesPage;
